import type {
    WebsocketConnection,
    WebsocketConnectionFactory,
    WebsocketConnectionOptions, WebsocketDynamicArgumentsResult
} from '../common/ws/types.ts';

export type WebSocketHeaderOptions = {
    headers?: Record<string, string>;
    getHeaders?: () => Record<string, string>;
    getWebSocketProtocols?: (headers?: Readonly<Record<string, string>>, protocols?: string | readonly string[]) => string | string[] | undefined | {
        protocols?: string | string[],
        headers?: Record<string, string>
    };
    getWebSocketSearchParams?: (headers?: Readonly<Record<string, string>>, search?: string) => Record<string, string>;
};

class WebsocketConnectionFactoryWrapper<T> implements WebsocketConnectionFactory<T> {

    #factory: WebsocketConnectionFactory<T>;
    #opts: WebSocketHeaderOptions;

    constructor(factory: WebsocketConnectionFactory<T>, opts: WebSocketHeaderOptions) {
        this.#factory = factory;
        this.#opts = opts;
    }

    create(uri: string, protocols?: string | string [], opts?: WebsocketConnectionOptions): WebsocketConnection<T> {
        return this.#factory.create(uri, protocols, this.#wrapWebsocketConnectionOptions(opts));
    }

    close() {
        this.#factory.close();
    }

    #wrapWebsocketConnectionOptions(opts?: WebsocketConnectionOptions) {
        const wrappedOpts = { ...opts };
        const dynamicArguments = opts?.dynamicArguments;
        wrappedOpts.dynamicArguments = this.#dynamicArguments(dynamicArguments);
        return wrappedOpts;
    }

    #dynamicArguments(dynamicArguments?: (url: string | URL, protocols?: string | readonly string[]) => WebsocketDynamicArgumentsResult): ((url: string | URL) => WebsocketDynamicArgumentsResult) | undefined {

        return (url: string | URL, protocols?: string | readonly string[]) => {
            const result = dynamicArguments?.(url, protocols) ?? {};
            const headers = {...this.#opts.headers};
            const dynamicHeaders = this.#opts.getHeaders ? this.#opts.getHeaders() : {};
            for (const [key, value] of Object.entries(dynamicHeaders)) {
                headers[key] = value;
            }
            url = new URL(result.url ?? url, result.baseUrl);

            const searchParams = this.#opts.getWebSocketSearchParams?.(headers, url.search);
            if (searchParams) {
                for (const [key, value] of Object.entries(searchParams)) {
                    url.searchParams.set(key, value);
                }
            }
            // cast is needed because getWebSocketProtocols is allowed to return both string[] and {protocols, headers}
            // {protocols, headers} case is not supported in browser environment, but most other WebSocket libraries support it
            result.protocols = this.#opts.getWebSocketProtocols?.(headers, result.protocols ?? protocols) as WebsocketDynamicArgumentsResult['protocols'];
            return result;
        };
    }
}
export const wrapWithHeaders = <T>(factory: WebsocketConnectionFactory<T>, opts?: WebSocketHeaderOptions): WebsocketConnectionFactory<T> => {
        if (opts) {
            if (opts.getWebSocketProtocols || opts.getWebSocketSearchParams || opts.getHeaders || opts.headers) {
                return new WebsocketConnectionFactoryWrapper(factory, opts)
            }
        }
        return factory;

}
